import { useCallback, useMemo, useState } from 'react';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Copy, Lock, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '~/components/ui/card';
import { Badge } from '~/components/ui/badge';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import type { AppLayoutContext } from '~/types';
import { schoolYearSettingsPath } from '~/pages/SchoolSettings/schoolYearSettingsPath';
import { useCoursesQuery } from '../SchoolYearView/useCoursesQuery';
import { useGenerateClassOfferings } from '../SchoolYearView/useGenerateClassOfferings';
import { useUpdateCourse } from '../SchoolYearView/useUpdateCourse';
import { useUpdateCourseTerms } from '../SchoolYearView/useUpdateCourseTerms';
import { useDeleteClassOffering } from '../SchoolYearView/useDeleteClassOffering';
import CourseGradebookDialog from './CourseGradebookDialog';
import { useDeleteCourseGradebook } from './useCourseGradebooks';
import CourseFormDialog from '../SchoolYearView/CourseFormDialog';
import type { CourseFromQuery, ClassOfferingFromQuery, TermFromQuery, InstructorFromQuery } from '../SchoolYearView/coursesTypes';
import CopyRosterDialog from './CopyRosterDialog';
import { useWayBack } from '~/hooks/useWayBack';

type CourseGradebookLine = CourseFromQuery['transcriptCourses'][number];

function CourseDetail() {
  const { schoolSlug, yearSlug, courseSlug } = useParams();
  const { school } = useOutletContext<AppLayoutContext>();
  const navigate = useNavigate();
  // Back past this course and its classes to wherever the person came from;
  // opened directly, up to the course list.
  const { goBack, toPreviousScreen } = useWayBack(
    `/app/${schoolSlug}/years/${yearSlug}/courses/${courseSlug}`,
    `/app/${schoolSlug}/years/${yearSlug}/courses`,
  );

  const schoolYear = school?.schoolYears?.find((y) => y.slug === yearSlug);
  const { data, loading, refetch } = useCoursesQuery(schoolYear?.id);
  const [generateOfferings, { loading: generating }] = useGenerateClassOfferings();
  const [updateCourse] = useUpdateCourse();
  const [updateCourseTerms] = useUpdateCourseTerms();
  const [deleteClassOffering] = useDeleteClassOffering();
  const [deleteCourseGradebook] = useDeleteCourseGradebook();
  const [editOpen, setEditOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<ClassOfferingFromQuery | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [copyRosterOpen, setCopyRosterOpen] = useState(false);
  const [gradebookDialogOpen, setGradebookDialogOpen] = useState(false);
  const [editingLine, setEditingLine] = useState<CourseGradebookLine | null>(null);
  const [deleteGradebookLine, setDeleteGradebookLine] = useState<CourseGradebookLine | null>(null);
  const [deleteGradebookConfirmation, setDeleteGradebookConfirmation] = useState('');
  const [deleteGradebookError, setDeleteGradebookError] = useState<string | null>(null);
  const [deletingGradebook, setDeletingGradebook] = useState(false);
  const availableGradeLevels = useMemo(
    () => ([...(data?.schoolYear?.school?.gradeLevelsSetting?.items ?? [])] as Array<{ description: string; order?: number }>)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((gradeLevel) => gradeLevel.description),
    [data]
  );

  const handleGenerate = useCallback(async (courseId: string) => {
    setPageError(null);
    setNotice(null);
    const result = await generateOfferings({ variables: { input: { courseId } } });
    const payload = result.data?.generateClassOfferings;
    if (payload?.userErrors?.length) {
      setPageError(payload.userErrors.map((error) => error.message).join(' '));
      return;
    }

    setNotice(payload?.message ?? 'Class reconciliation finished.');
    await refetch();
  }, [generateOfferings, refetch]);

  const handleConfigureTerms = useCallback(() => {
    setEditOpen(false);
    if (schoolSlug) navigate(schoolYearSettingsPath(schoolSlug, yearSlug));
  }, [navigate, schoolSlug, yearSlug]);

  const handleSave = useCallback(async (formData: any) => {
    const updateResult = await updateCourse({
      variables: {
        input: {
          id: formData.id,
          name: formData.name,
          subtitle: formData.subtitle || null,
          catalogCode: formData.catalogCode || null,
          description: formData.description || null,
          defaultSections: formData.defaultSections,
          gradeLevelKeys: formData.gradeLevelKeys,
        },
      },
    });
    const updateErrors = updateResult.data?.updateCourse?.userErrors;
    if (updateErrors?.length) {
      throw new Error(updateErrors.map((error) => error.message).join(', '));
    }

    const termsResult = await updateCourseTerms({
      variables: { input: { courseId: formData.id, termIds: formData.termIds } },
    });
    const termErrors = termsResult.data?.updateCourseTerms?.userErrors;
    if (termErrors?.length) {
      throw new Error(termErrors.map((error) => error.message).join(' '));
    }

    setNotice('Course details and term selections were updated.');
    await refetch();
  }, [refetch, updateCourse, updateCourseTerms]);

  const openDeleteConfirmation = useCallback((offering: ClassOfferingFromQuery) => {
    setDeleteError(null);
    setDeleteConfirm(offering);
  }, []);

  const handleDeleteClass = useCallback(async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const result = await deleteClassOffering({ variables: { input: { id: deleteConfirm.id } } });
      const payload = result.data?.deleteClassOffering;
      if (!payload?.success) {
        setDeleteError(payload?.userErrors?.map((error) => error.message).join(' ') || 'Unable to remove the class.');
        return;
      }

      setDeleteConfirm(null);
      setNotice(`${deleteConfirm.displayName} was removed.`);
      await refetch();
    } finally {
      setDeleting(false);
    }
  }, [deleteClassOffering, deleteConfirm, refetch]);

  const handleDeleteGradebook = useCallback(async () => {
    if (!deleteGradebookLine) return;

    setDeletingGradebook(true);
    setDeleteGradebookError(null);
    try {
      const result = await deleteCourseGradebook({
        variables: {
          input: {
            id: deleteGradebookLine.id,
            confirmationName: deleteGradebookConfirmation,
          },
        },
      });
      const payload = result.data?.deleteCourseGradebook;
      if (!payload?.success) {
        setDeleteGradebookError(
          payload?.userErrors?.map((error) => error.message).join(' ') ||
            'Unable to delete the gradebook.'
        );
        return;
      }

      const deletedName = deleteGradebookLine.name;
      setDeleteGradebookLine(null);
      setDeleteGradebookConfirmation('');
      setNotice(`${deletedName} was deleted.`);
      await refetch();
    } catch (caught) {
      setDeleteGradebookError(
        caught instanceof Error ? caught.message : 'Unable to delete the gradebook.'
      );
    } finally {
      setDeletingGradebook(false);
    }
  }, [deleteCourseGradebook, deleteGradebookConfirmation, deleteGradebookLine, refetch]);

  if (!school || !schoolYear) {
    return <div className="p-6"><p>Loading...</p></div>;
  }

  if (loading) {
    return <div className="p-6"><p>Loading course...</p></div>;
  }

  const courses = (data?.schoolYear?.courses ?? []) as CourseFromQuery[];
  const course = courses.find((c) => c.slug === courseSlug);

  if (!course) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>Course Not Found</CardTitle>
            <CardDescription>The course you're looking for doesn't exist.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const allTerms = (data?.schoolYear?.terms ?? []) as TermFromQuery[];
  const courseTermIds = new Set(course.terms.map((t) => t.id));
  const offerings = course.classOfferings as ClassOfferingFromQuery[];
  const hasOfferings = offerings.length > 0;

  // Group offerings by term
  const offeringsByTerm = new Map<string, ClassOfferingFromQuery[]>();
  for (const offering of offerings) {
    const termId = offering.term?.id;
    if (!termId) continue;
    if (!offeringsByTerm.has(termId)) offeringsByTerm.set(termId, []);
    offeringsByTerm.get(termId)!.push(offering);
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={goBack}
          aria-label={toPreviousScreen ? 'Back' : 'Back to courses'}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h2 className="text-3xl font-bold">{course.name}</h2>
          <p className="text-muted-foreground">
            {course.catalogCode && <span>{course.catalogCode} — </span>}
            {course.subtitle || schoolYear.name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setEditOpen(true)}>
            <Pencil className="h-4 w-4 mr-1" />
            Edit Course &amp; Terms
          </Button>
          {courseTermIds.size > 0 && (
            <Button onClick={() => handleGenerate(course.id)} disabled={generating}>
              <RefreshCw className={`h-4 w-4 mr-1 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Checking...' : 'Create Missing Classes'}
            </Button>
          )}
        </div>
      </div>

      {notice && (
        <div role="status" className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
          {notice}
        </div>
      )}
      {pageError && (
        <div role="alert" className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {pageError}
        </div>
      )}

      {/* Course Info */}
      <Card>
        <CardHeader>
          <CardTitle>Course Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Catalog Code</span>
              <p className="font-medium">{course.catalogCode || '—'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Default Sections</span>
              <p className="font-medium">{course.defaultSections}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Grade Levels</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {course.gradeLevelKeys.length > 0
                  ? course.gradeLevelKeys.map((g) => (
                      <Badge key={g} variant="secondary">{g}</Badge>
                    ))
                  : <span className="font-medium">All grades</span>}
              </div>
            </div>
            <div>
              <span className="text-muted-foreground">Description</span>
              <p className="font-medium">{course.description || '—'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gradebooks — defined on the course, instantiated per class */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Gradebooks</CardTitle>
              <CardDescription>
                {course.transcriptCourses.length === 0
                  ? 'Define a gradebook once for the course and apply it to any classes.'
                  : `${course.transcriptCourses.length} line${course.transcriptCourses.length !== 1 ? 's' : ''} on the transcript, each with its own credit`}
              </CardDescription>
            </div>
            {hasOfferings && (
              <Button variant="outline" size="sm" onClick={() => { setEditingLine(null); setGradebookDialogOpen(true); }}>
                <Plus className="h-4 w-4 mr-1" />
                Add Gradebook
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {course.transcriptCourses.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No gradebooks yet.{hasOfferings ? '' : ' Generate classes first.'}
            </p>
          ) : (
            <div className="space-y-2">
              {course.transcriptCourses.map((tc) => (
                <div key={tc.id} className="flex items-center justify-between rounded-md border px-3 py-2">
                  <div>
                    <span className="text-sm font-medium">{tc.name}</span>
                    <p className="text-xs text-muted-foreground">
                      {tc.creditValue == null ? 'No credit set' : `${tc.creditValue} credit`}
                      {tc.termCount > 0 && ` · ${tc.termCount} term${tc.termCount !== 1 ? 's' : ''}`}
                      {tc.categories.length > 0 && ` · ${tc.categories.map((c) => `${c.name} ${c.weight}%`).join(', ')}`}
                    </p>
                    {tc.categories.some((category) => category.locked) && (
                      <Badge variant="secondary" className="mt-1 gap-1">
                        <Lock className="h-3 w-3" />
                        Categories locked
                        <span className="sr-only">
                          {' '}by {[...new Set(tc.categories.flatMap((category) =>
                            category.lockedByGradebookNames
                          ))].join(', ')}
                        </span>
                      </Badge>
                    )}
                    {tc.gradingTemplate && (
                      <p className={`text-xs ${tc.gradingTemplateOutOfDate ? 'text-amber-700' : 'text-muted-foreground'}`}>
                        {tc.gradingTemplate.name} preset
                        {tc.gradingTemplateOutOfDate ? ' · update available' : ' · up to date'}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Edit ${tc.name}`}
                      onClick={() => { setEditingLine(tc); setGradebookDialogOpen(true); }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground hover:text-destructive"
                      aria-label={`Delete ${tc.name}`}
                      onClick={() => {
                        setDeleteGradebookError(null);
                        setDeleteGradebookConfirmation('');
                        setDeleteGradebookLine(tc);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Classes by Term */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Classes by Term</CardTitle>
              <CardDescription>
                {offerings.length} class{offerings.length !== 1 ? 'es' : ''} across {courseTermIds.size} term{courseTermIds.size !== 1 ? 's' : ''}
              </CardDescription>
            </div>
            {hasOfferings && (
              offerings.length > 1 && (
                <Button variant="outline" size="sm" onClick={() => setCopyRosterOpen(true)}>
                  <Copy className="h-4 w-4 mr-1" />
                  Copy Roster
                </Button>
              )
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasOfferings && courseTermIds.size === 0 && (
            <div className="rounded-lg border border-dashed p-8 text-center space-y-3">
              {allTerms.length === 0 ? (
                <>
                  <p className="font-medium">This school year has no academic terms yet.</p>
                  <p className="text-sm text-muted-foreground">
                    Configure terms, then return here to select them for this course.
                  </p>
                  <Button variant="outline" onClick={handleConfigureTerms}>
                    <Calendar className="h-4 w-4 mr-1" />
                    Configure Academic Terms
                  </Button>
                </>
              ) : (
                <>
                  <p className="font-medium">No terms are selected for this course.</p>
                  <p className="text-sm text-muted-foreground">
                    Edit the course and choose the terms in which it will run.
                  </p>
                  <Button variant="outline" onClick={() => setEditOpen(true)}>
                    <Pencil className="h-4 w-4 mr-1" />
                    Select Course Terms
                  </Button>
                </>
              )}
            </div>
          )}
          {!hasOfferings && courseTermIds.size > 0 && (
            <div className="rounded-lg border border-dashed p-8 text-center space-y-2">
              <p className="text-muted-foreground">
                No classes exist yet. Use "Create Missing Classes" to create {course.defaultSections} section{course.defaultSections !== 1 ? 's' : ''} for each selected term.
              </p>
            </div>
          )}
          {allTerms.map((term) => {
            if (!courseTermIds.has(term.id)) return null;
            const termOfferings = offeringsByTerm.get(term.id) ?? [];

            return (
              <div key={term.id}>
                <h4 className="font-medium text-sm text-muted-foreground mb-2">{term.name}</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {termOfferings.length > 0 ? (
                    termOfferings
                      .sort((a, b) => a.section.localeCompare(b.section))
                      .map((offering) => (
                        <Card
                          key={offering.id}
                          className="hover:border-primary/50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/app/${schoolSlug}/years/${yearSlug}/courses/${courseSlug}/classes/${offering.id}`)}
                        >
                          <CardContent className="flex items-start justify-between gap-3 p-4">
                            <div>
                              <p className="font-medium">Section {offering.section}</p>
                              <p className="text-sm text-muted-foreground mt-1">
                                {offering.activeStudentCount ?? 0}{offering.capacity ? `/${offering.capacity}` : ''} students
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {offering.instructors.length > 0
                                  ? offering.instructors.map((i: InstructorFromQuery) => `${i.person.firstName} ${i.person.lastName}`).join(', ')
                                  : 'No instructor'}
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                              aria-label={`Remove ${offering.displayName}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                openDeleteConfirmation(offering);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </CardContent>
                        </Card>
                      ))
                  ) : (
                    <Card className="border-dashed col-span-full">
                      <CardContent className="p-4 text-center text-sm text-muted-foreground">
                        No classes for this term yet
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <CourseFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        onSave={handleSave}
        course={course}
        availableTerms={allTerms}
        availableGradeLevels={availableGradeLevels}
        onConfigureTerms={handleConfigureTerms}
      />

      <AlertDialog
        open={!!deleteConfirm}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteConfirm(null);
            setDeleteError(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Class Instance</AlertDialogTitle>
            <AlertDialogDescription>
              Remove &ldquo;{deleteConfirm?.displayName}&rdquo;? Only a completely empty class can be
              removed. Students, instructors, assignments, attendance, and gradebook data are never deleted automatically.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && (
            <div role="alert" className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {deleteError}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteClass}
              disabled={deleting}
            >
              {deleting ? 'Removing...' : 'Remove Empty Class'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CourseGradebookDialog
        key={editingLine?.id ?? 'new'}
        open={gradebookDialogOpen}
        onOpenChange={(open) => { setGradebookDialogOpen(open); if (!open) setEditingLine(null); }}
        editing={editingLine}
        schoolId={school.id}
        courseId={course.id}
        terms={course.terms}
        classOfferings={offerings}
        transcriptCourses={course.transcriptCourses}
        onCreated={async () => {
          await refetch();
          setPageError(null);
        }}
        onRefreshError={(message) => setPageError(message)}
      />

      <AlertDialog
        open={!!deleteGradebookLine}
        onOpenChange={(open) => {
          if (!open && !deletingGradebook) {
            setDeleteGradebookLine(null);
            setDeleteGradebookConfirmation('');
            setDeleteGradebookError(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete course gradebook</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the shared definition and its empty class gradebooks.
              Assignments, finalized results, and benchmark work block deletion. Type{' '}
              <strong>{deleteGradebookLine?.name}</strong> to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="delete-course-gradebook-confirmation">Gradebook name</Label>
            <Input
              id="delete-course-gradebook-confirmation"
              value={deleteGradebookConfirmation}
              onChange={(event) => setDeleteGradebookConfirmation(event.target.value)}
              autoComplete="off"
            />
            {deleteGradebookError && (
              <div role="alert" className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                {deleteGradebookError}
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingGradebook}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteGradebook}
              disabled={
                deletingGradebook ||
                deleteGradebookConfirmation !== deleteGradebookLine?.name
              }
            >
              {deletingGradebook ? 'Deleting...' : 'Delete Empty Gradebook'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CopyRosterDialog
        open={copyRosterOpen}
        onOpenChange={setCopyRosterOpen}
        onComplete={refetch}
        offerings={offerings}
      />
    </div>
  );
}

export default CourseDetail;
